import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import logo from '../../assets/logo.jpg';
import tiktokIcon from '../../assets/icons/tiktok.webp';
import { 
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  Divider,
  MenuItem,
  Paper,
  ClickAwayListener,
  Grow,
  Popper,
  MenuList,
  useTheme,
  Container,
  Typography,
  Stack,
  alpha,
  Tooltip,
  keyframes
} from '@mui/material';
import {
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Facebook,
  WhatsApp,
} from '@mui/icons-material';

// Enhanced color scheme
const colors = {
  primary: '#0056b3',
  secondary: '#003366',
  accent: '#ff6600',
  textPrimary: '#2c3e50',
  textSecondary: '#7f8c8d',
  background: '#ffffff',
  hoverBg: 'rgba(0, 86, 179, 0.05)',
  activeBg: 'rgba(0, 86, 179, 0.1)',
  border: '#e0e0e0',
  divider: '#ecf0f1',
  topBarBg: '#f8f9fa',
  topBarText: '#555555',
  navBarBg: '#ffffff',
  navText: '#000000'
};

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 86, 179, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(0, 86, 179, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 86, 179, 0); }
`;

const contactInfo = [
  { 
    icon: <PhoneIcon sx={{ color: 'white' }} />, 
    text: '(+975) 17171136',
    label: 'Phone',
    color: colors.primary,
    bgColor: alpha(colors.primary, 0.9)
  },
  { 
    icon: <EmailIcon sx={{ color: 'white' }} />, 
    text: 'koufukucustomercare@gmail.com',
    label: 'Email',
    color: colors.accent,
    bgColor: alpha(colors.accent, 0.9)
  },
  { 
    icon: <LocationIcon sx={{ color: 'white' }} />, 
    text: 'Chenangri, Trashigang, Bhutan',
    label: 'Location',
    color: colors.secondary,
    bgColor: alpha(colors.secondary, 0.9)
  }
];

const socialMedia = [
  { 
    icon: <Facebook sx={{ color: 'white', fontSize: '25px' }} />, 
    url: 'https://www.facebook.com/bhutandairyproducts/',
    label: 'Facebook',
    color: '#1877F2',
    hoverColor: '#0D5BD2',
    pulseColor: '#4285F4'
  },
  { 
    icon: <WhatsApp sx={{ color: 'white', fontSize: '25px' }} />, 
    url: `https://wa.me/17171136?text=${encodeURIComponent("Hello Koufuku, I have a question about your products.")}`,
    label: 'WhatsApp',
    color: '#25D366',
    hoverColor: '#128C7E',
    pulseColor: '#34B7F1'
  },
  { 
    icon: <img src={tiktokIcon} alt="TikTok" style={{ width: 25, height: 25 }} />, 
    url: 'https://www.tiktok.com/@koufouku.internat', 
    label: 'TikTok',
    color: '#000000',
    hoverColor: '#FE2C55',
    pulseColor: '#FF0050'
  }
];

const ContactInfoSlider = () => {
  const [offset, setOffset] = useState(0);
  const itemWidth = 300;

  useEffect(() => {
    const interval = setInterval(() => {
      setOffset((prev) => (prev + 1) % (contactInfo.length * itemWidth));
    }, 30);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      width: '100%',
      height: 60,
      position: 'relative',
      mx: 3,
      backgroundColor: alpha(colors.primary, 0.03),
      borderRadius: 2,
      border: `1px solid ${alpha(colors.divider, 0.5)}`,
    }}>
      <Box sx={{
        display: 'flex',
        position: 'absolute',
        left: -offset,
        transition: 'left 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        gap: 4,
        willChange: 'transform',
        alignItems: 'center',
        height: '100%',
      }}>
        {[...contactInfo, ...contactInfo].map((item, index) => (
          <Box 
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: itemWidth,
              gap: 2.5,
              px: 2,
              height: '100%',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: '50%',
              backgroundColor: item.bgColor,
              flexShrink: 0,
              color: 'white',
              animation: `${floatAnimation} 4s ease-in-out infinite`,
              animationDelay: `${index * 0.5}s`,
              '& svg': {
                fontSize: '1.2rem'
              }
            }}>
              {item.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ 
                color: colors.textPrimary,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.3,
                fontSize: '0.95rem',
                letterSpacing: '0.02em'
              }}>
                {item.text}
              </Typography>
              <Typography variant="caption" sx={{ 
                color: item.color,
                display: 'block',
                lineHeight: 1.4,
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase'
              }}>
                {item.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const Header = () => {
  const theme = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState(null);

  const mainNavItems = [
    { 
      label: 'Home', 
      path: '/',
      exact: true
    },
    { 
      label: 'About Us', 
      path: '#',
      subMenu: [
        { label: 'Mission, Vision & Values', path: '/about/mission' },
        { label: 'Board of Directors', path: '/about/board-of-directors' },
        { label: 'Organization Structure', path: '/about/organization-structure' },
        { label: 'Download Reports', path: '/about/reports' },
      ]
    },
    { 
      label: 'Products', 
      path: '#',
      subMenu: [
        { label: 'View Products List', path: '/products/products-list' },
        { label: 'Place Your Order', path: '/order' },
      ]
    },
    { 
      label: 'KIL Offers', 
      path: '/offers',
    },
    { 
      label: 'Announcements', 
      path: '#',
      subMenu: [
        { label: 'KIL Vacancy', path: '/announcements/vacancies' },
        { label: 'KIL Tender', path: '/announcements/tenders' },
      ]
    },
    { 
      label: 'KIL Gallerys', 
      path: '/gallery', 
    },
    { 
      label: 'Distributors', 
      path: '/dealer',
    },
    { 
      label: 'Contact Us', 
      path: '/contact'
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    if (mobileOpen) {
      setMobileExpandedMenu(null);
    }
  };

  const handleMobileMenuToggle = (itemLabel) => {
    setMobileExpandedMenu(mobileExpandedMenu === itemLabel ? null : itemLabel);
  };

  const isActive = (path, exact = false, subMenu = []) => {
    const isPathActive = exact 
      ? location.pathname === path
      : location.pathname.startsWith(path);
    
    const isSubMenuActive = subMenu.some(subItem => 
      location.pathname.startsWith(subItem.path)
    );
    
    return isPathActive || isSubMenuActive;
  };

  const handleSubMenuOpen = (event, itemLabel) => {
    setOpenSubMenu(itemLabel);
    setAnchorEl(event.currentTarget);
  };

  const handleSubMenuClose = () => {
    setOpenSubMenu(null);
    setAnchorEl(null);
  };

  const drawer = (
    <Box sx={{ width: 280 }} role="presentation">
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <Box
          component="img"
          src={logo}
          alt="KIL Logo"
          sx={{ 
            height: 50,
            width: 'auto',
            objectFit: 'contain'
          }}
        />
      </Box>
      <Divider />
      <List>
        {mainNavItems.map((item) => (
          <React.Fragment key={item.label}>
            <ListItem disablePadding>
              <Button
                fullWidth
                component={item.subMenu ? 'div' : RouterLink} 
                to={!item.subMenu ? item.path : undefined}
                onClick={() => {
                  if (item.subMenu) {
                    handleMobileMenuToggle(item.label);
                  } else if (item.path !== '#') {
                    handleDrawerToggle();
                  }
                }}
                sx={{
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 1.5,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontSize: '0.75rem',
                  color: isActive(item.path, item.exact, item.subMenu) ? colors.primary : colors.navText,
                  fontWeight: 600,
                  backgroundColor: isActive(item.path, item.exact, item.subMenu) ? colors.activeBg : 'transparent',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    width: isActive(item.path, item.exact, item.subMenu) ? '80%' : '0%',
                    height: '2px',
                    backgroundColor: colors.primary,
                    transition: 'all 0.3s ease',
                    transform: 'translateX(-50%)'
                  },
                  '&:hover::after': {
                    width: '80%'
                  }
                }}
              >
                {item.label}
              </Button>
            </ListItem>
            {item.subMenu && mobileExpandedMenu === item.label && (
              <Box sx={{ pl: 2 }}>
                {item.subMenu.map((subItem) => (
                  <ListItem key={subItem.label} disablePadding>
                    <Button
                      fullWidth
                      component={RouterLink} 
                      to={subItem.path}
                      sx={{ 
                        pl: 4,
                        py: 1.5,
                        justifyContent: 'flex-start',
                        fontSize: '0.875rem',
                        color: isActive(subItem.path) ? colors.primary : colors.navText,
                        fontWeight: isActive(subItem.path) ? 600 : 500,
                        backgroundColor: isActive(subItem.path) ? colors.activeBg : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: alpha(colors.primary, 0.08),
                          paddingLeft: '28px'
                        }
                      }}
                      onClick={handleDrawerToggle}
                    >
                      {subItem.label}
                    </Button>
                  </ListItem>
                ))}
              </Box>
            )}
          </React.Fragment>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
        {socialMedia.map((social, index) => (
          <Tooltip key={index} title={social.label} arrow>
            <IconButton 
              component="a"
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                width: 50,
                height: 50,
                backgroundColor: alpha(social.color, 0.9),
                color: 'white',
                transition: 'all 0.3s ease',
                animation: `${floatAnimation} 4s ease-in-out infinite`,
                animationDelay: `${index * 0.2}s`,
                '&:hover': {
                  backgroundColor: alpha(social.hoverColor, 0.9),
                  transform: 'scale(1.1)',
                  animation: `${pulseAnimation} 1.5s infinite`,
                }
              }}
            >
              {social.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );

  return (
    <AppBar 
      position="sticky" 
      elevation={0} 
      sx={{ 
        backgroundColor: colors.background,
        borderBottom: `1px solid ${colors.divider}`,
      }}
    >
      <Container maxWidth="lg" disableGutters>
        <Toolbar sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          px: { xs: 2, md: 0 },
          py: 1,
          minHeight: '80px !important'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            flexShrink: 0,
            position: 'relative',
            '&:hover': {
              '& .logo-circle': {
                transform: 'scale(1.05)',
                boxShadow: `0 0 0 2px ${colors.primary}, 0 4px 12px rgba(0, 86, 179, 0.2)`
              },
              '& .logo-glow': {
                opacity: 0.4
              }
            }
          }}>
            <RouterLink to="/">
              <Box
                className="logo-circle"
                sx={{
                  width: { xs: 60, sm: 70 },
                  height: { xs: 60, sm: 70 },
                  borderRadius: '50%',
                  overflow: 'hidden', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'white',
                  border: `1px solid ${alpha(colors.primary, 0.1)}`,
                  transition: 'all 0.4s cubic-bezier(0.65, 0, 0.35, 1)',
                  boxShadow: '1 1px 1px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="KIL Logo"
                  sx={{ 
                    width: '80%',
                    height: '80%',
                    objectFit: 'contain',
                    transition: 'transform 0.3s ease',
                  }}
                />
              </Box>
            </RouterLink>
            
            <Box
              className="logo-glow"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'calc(100% + 16px)',
                height: 'calc(100% + 16px)',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(colors.primary, 0.2)} 0%, transparent 70%)`,
                opacity: 0,
                transition: 'opacity 0.4s ease',
                zIndex: 0,
                pointerEvents: 'none'
              }}
            />
          </Box>
          
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            flex: 1,
            minWidth: 0,
            mx: 2
          }}>
            <ContactInfoSlider />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0
          }}>
            {socialMedia.map((social, index) => (
              <Tooltip key={index} title={social.label} arrow>
                <IconButton 
                  component="a"
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="medium"
                  sx={{
                    width: 44,
                    height: 44,
                    backgroundColor: alpha(social.color, 0.9),
                    color: 'white',
                    transition: 'all 0.3s ease',
                    animation: `${floatAnimation} 4s ease-in-out infinite`,
                    animationDelay: `${index * 0.2}s`,
                    '&:hover': {
                      backgroundColor: alpha(social.hoverColor, 0.9),
                      transform: 'scale(1.1)',
                      animation: `${pulseAnimation} 1.5s infinite`,
                    }
                  }}
                >
                  {social.icon}
                </IconButton>
              </Tooltip>
            ))}
            
            <IconButton
              color="inherit"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ 
                display: { md: 'none' },
                ml: 1,
                color: colors.textPrimary,
                '&:hover': {
                  backgroundColor: alpha(colors.primary, 0.1)
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
        
        <Box sx={{ 
          display: { xs: 'none', md: 'block' },
          borderTop: `1px solid ${colors.divider}`
        }}>
          <Stack direction="row" spacing={0}>
            {mainNavItems.map((item) => (
              <Box 
                key={item.label} 
                sx={{ position: 'relative' }}
                onMouseEnter={(e) => handleSubMenuOpen(e, item.label)}
                onMouseLeave={handleSubMenuClose}
              >
                <Button
                  component={item.path === '#' ? 'div' : RouterLink}
                  to={item.path !== '#' ? item.path : undefined}
                  sx={{
                    px: 2.5,
                    py: 1.8,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontSize: '0.75rem',
                    color: isActive(item.path, item.exact, item.subMenu) ? colors.primary : colors.navText,
                    fontWeight: 600,
                    borderRadius: 0,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: isActive(item.path, item.exact, item.subMenu) ? '80%' : '0%',
                      height: '2px',
                      backgroundColor: colors.primary,
                      transition: 'all 0.3s ease',
                      transform: 'translateX(-50%)'
                    },
                    '&:hover::after': {
                      width: '80%'
                    },
                    '&:hover': {
                      color: colors.primary,
                      backgroundColor: 'transparent'
                    }
                  }}
                  endIcon={item.subMenu ? (openSubMenu === item.label ? <ExpandLessIcon /> : <ExpandMoreIcon />) : null}
                  onClick={(e) => {
                    if (item.path === '#') {
                      e.preventDefault();
                    }
                  }}
                >
                  {item.label}
                </Button>
              
                {item.subMenu && (
                  <Popper
                    open={openSubMenu === item.label}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    transition
                    disablePortal
                    sx={{ 
                      zIndex: theme.zIndex.modal,
                    }}
                  >
                    {({ TransitionProps }) => (
                      <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
                        <Paper elevation={3} sx={{ 
                          borderRadius: '0 0 4px 4px',
                          overflow: 'hidden',
                          minWidth: 200
                        }}>
                          <ClickAwayListener onClickAway={handleSubMenuClose}>
                            <MenuList autoFocusItem={openSubMenu === item.label}>
                              {item.subMenu.map((subItem) => (
                                <MenuItem 
                                  key={subItem.label}
                                  component={RouterLink}
                                  to={subItem.path}
                                  onClick={handleSubMenuClose}
                                  sx={{
                                    px: 2.5,
                                    py: 1.5,
                                    fontSize: '0.875rem',
                                    color: isActive(subItem.path) ? colors.primary : colors.navText,
                                    fontWeight: isActive(subItem.path) ? 600 : 500,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: alpha(colors.primary, 0.08),
                                      paddingLeft: '32px'
                                    }
                                  }}
                                >
                                  {subItem.label}
                                </MenuItem>
                              ))}
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
        
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: 280,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Container>
    </AppBar>
  );
};

export default Header;